import {
	queryOptions,
	useQuery,
	useSuspenseQueries,
	useSuspenseQuery,
} from '@tanstack/react-query'

import { actionQueries } from '@/features/actions/queries'

import { successStaleTime } from '@/shared/api/utils/query'
import { useCurrentTenant } from '@/shared/auth/store'
import { time } from '@/shared/datetime/utils/time'

import {
	fetchPlaybookActionDetails,
	fetchPlaybookActionPrompt,
	fetchPlaybookActivityDetails,
	fetchTenantPlaybooks,
	fetchWorkflowDefinition,
} from './api'
import { PLAYBOOK_WAITING_STATES } from './constants/workflow'

import type { Playbook, PlaybookWithActions } from './types/workflow'
import type { Action } from '@/features/actions/types'
import type { UseSuspenseQueryResult } from '@tanstack/react-query'

export const playbookKeys = {
	all: ['playbooks'] as const,

	lists: () => [...playbookKeys.all, 'list'] as const,
	list: (tenant: string) => [...playbookKeys.lists(), tenant] as const,

	definitions: () => [...playbookKeys.all, 'definition'] as const,
	definition: (tenant: string, playbookName: string) =>
		[...playbookKeys.definitions(), tenant, playbookName] as const,

	activities: () => [...playbookKeys.all, 'activity'] as const,
	activityDetails: (tenant: string, playbookName: string | null) =>
		[...playbookKeys.activities(), tenant, playbookName, 'no-cache'] as const,

	collapsibles: () => [...playbookKeys.all, 'collapsible'] as const,
	collapsible: (
		tenant: string,
		playbookName: string,
		workflowActionId: string,
	) =>
		[
			...playbookKeys.collapsibles(),
			tenant,
			playbookName,
			workflowActionId,
			'no-cache',
		] as const,

	prompts: () => [...playbookKeys.all, 'prompts'] as const,
	prompt: (tenant: string, playbookName: string, workflowActionId: string) =>
		[
			...playbookKeys.prompts(),
			tenant,
			playbookName,
			workflowActionId,
			'no-cache',
		] as const,
}

export const playbookQueries = {
	/**
	 * List of playbooks available for the tenant
	 * @param tenant - The tenant ID
	 */
	list: (tenant: string) =>
		queryOptions({
			queryKey: playbookKeys.list(tenant),
			queryFn: ({ signal }) => fetchTenantPlaybooks(tenant, signal),
			staleTime: ({ state }) => successStaleTime(state, 1, 'h'),
			gcTime: time(1, 'h'),
		}),

	/**
	 * Fetch the workflow definition for a playbook
	 * @param tenant - The tenant ID
	 * @param playbookName - The playbook name
	 */
	definition: (tenant: string, playbookName: string) =>
		queryOptions({
			queryKey: playbookKeys.definition(tenant, playbookName),
			queryFn: ({ signal }) =>
				fetchWorkflowDefinition(tenant, playbookName, signal),
			// Intentionally disables cache, alwasy refetch
			staleTime: ({ state }) => successStaleTime(state, 1, 'm'),
			gcTime: time(5, 'm'),
			enabled: !!tenant && !!playbookName,
		}),

	/**
	 * Fetch the activity details for a playbook
	 * @param tenant - Tenant ID
	 * @param playbookName - Playbook name
	 */
	activityDetails: (tenant: string, playbookName: string | null) =>
		queryOptions({
			queryKey: playbookKeys.activityDetails(tenant, playbookName),
			queryFn: ({ signal }) =>
				fetchPlaybookActivityDetails(tenant, playbookName, signal),
			// Intentionally disables cache, alwasy refetch
			staleTime: 0,
			gcTime: 0,
			refetchInterval: ({ state }) => {
				const summary = state.data?.summary

				if (!summary) return false

				const shouldRefetch = PLAYBOOK_WAITING_STATES.some(
					(state) => state === summary.status,
				)

				const hasPendingActions = summary.actions_summary.actions_summary.some(
					(action) =>
						action.status === 'pending' || action.status === 'running',
				)

				return shouldRefetch || hasPendingActions ? time(5, 's') : false
			},
			enabled: !!tenant && !!playbookName,
		}),

	/**
	 * Fetch the action result form a playbook collapsible
	 * @param tenant - Tenant ID
	 * @param playbookName - Playbook name
	 * @param workflowActionId  - Action ID
	 */
	collapsible: (
		tenant: string,
		playbookName: string,
		workflowActionId: string,
	) =>
		queryOptions({
			queryKey: playbookKeys.collapsible(
				tenant,
				playbookName,
				workflowActionId,
			),
			queryFn: ({ signal }) =>
				fetchPlaybookActionDetails(
					tenant,
					playbookName,
					workflowActionId,
					signal,
				),
			staleTime: ({ state }) => successStaleTime(state, 2, 'm'),
			gcTime: time(30, 'm'),
			enabled: !!tenant && !!playbookName && !!workflowActionId,
		}),

	/**
	 * Fetch the action prompt for a playbook
	 * @param tenant - Tenant ID
	 * @param playbookName - Playbook name
	 * @param workflowActionId - Action ID
	 */
	prompt: (tenant: string, playbookName: string, workflowActionId: string) =>
		queryOptions({
			queryKey: playbookKeys.prompt(tenant, playbookName, workflowActionId),
			queryFn: ({ signal }) =>
				fetchPlaybookActionPrompt(
					tenant,
					playbookName,
					workflowActionId,
					signal,
				),
			staleTime: ({ state }) => {
				if (state.status !== 'success') return 0

				const canCache = !!state.data
				return canCache ? time(1, 'h') : time(10, 's')
			},
			gcTime: time(1, 'h'),
			enabled: !!tenant && !!playbookName && !!workflowActionId,
		}),
}

/**
 * Custom hook to fetch Playbooks
 */
export const usePlaybookList = () => {
	const tenant = useCurrentTenant()

	return useSuspenseQuery(playbookQueries.list(tenant))
}

/**
 * Custom hook to fetch the tenant's Playbooks with each of their actions
 * resolved against the actions catalog.
 *
 * The playbook list only carries action **ids**, so any consumer showing action
 * names depends on two independent queries. Joining them here keeps that
 * dependency in one place — notably so `refetch` refreshes *both*; refetching
 * playbooks alone leaves the action names stale.
 */
export const usePlaybookListWithActions = () => {
	const tenant = useCurrentTenant()

	return useSuspenseQueries({
		queries: [playbookQueries.list(tenant), actionQueries.list(tenant)],
		combine: _combinePlaybooksWithActions,
	})
}

/**
 * Custom hook to fetch Playbook Definition synchronously (e.g. require playbookName to enable the query)
 * @param playbookName - The playbook name
 */
export const usePlaybookDefinitionSync = (
	playbookName: string,
	enabled: boolean = true,
) => {
	const tenant = useCurrentTenant()

	return useQuery({
		...playbookQueries.definition(tenant, playbookName),
		enabled: !!tenant && !!playbookName && enabled,
	})
}

/**
 * Custom hook to fetch Playbook Activity Details
 * @param playbookName - The playbook name
 */
export const usePlaybookActivityDetails = (playbookName: string | null) => {
	const tenant = useCurrentTenant()

	return useSuspenseQuery(playbookQueries.activityDetails(tenant, playbookName))
}

/**
 * Custom hook to fetch Playbook Collapsible Data
 * @param {string} playbookname - Playbook name
 * @param {string} workflowActionId - Action ID
 */
export const usePlaybookCollapsibleData = (
	playbookName: string,
	workflowActionId: string,
) => {
	const tenant = useCurrentTenant()

	return useSuspenseQuery(
		playbookQueries.collapsible(tenant, playbookName, workflowActionId),
	)
}

/**
 * Custom hook to fetch playbook action prompt
 * @param playbookName - Playbook name
 * @param workflowActionId - Action ID
 */
export const usePlaybookActionPrompt = (
	playbookName: string,
	workflowActionId: string,
) => {
	const tenant = useCurrentTenant()

	return useQuery(
		playbookQueries.prompt(tenant, playbookName, workflowActionId),
	)
}

/**
 * Joins the playbook list with the actions catalog.
 *
 * Declared at module scope so its identity is stable across renders — TanStack
 * re-runs `combine` whenever it changes, which would defeat the memoization.
 * @param playbooks - Result of the playbook list query
 * @param actions - Result of the actions catalog query
 */
function _combinePlaybooksWithActions([playbooks, actions]: [
	UseSuspenseQueryResult<Array<Playbook>>,
	UseSuspenseQueryResult<Array<Action>>,
]) {
	const actionsById = new Map(
		actions.data.map((action) => [action.action_id, action]),
	)

	const data: Array<PlaybookWithActions> = playbooks.data.map((playbook) => ({
		...playbook,
		resolvedActions: playbook.actions.flatMap(
			(actionId) => actionsById.get(actionId) ?? [],
		),
	}))

	return {
		data,
		// Oldest of the two: the join is only as fresh as its stalest half
		dataUpdatedAt: Math.min(playbooks.dataUpdatedAt, actions.dataUpdatedAt),
		isFetching: playbooks.isFetching || actions.isFetching,
		refetch: async () => {
			await Promise.all([playbooks.refetch(), actions.refetch()])
		},
	}
}
